import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { Calendar, Droplets, Heart, FileText, Save, X } from 'lucide-react';
import { useCycle } from '../../context/CycleContext';

interface CycleLogFormProps {
  logId: number | null;
  selectedDate?: Date;
  onClose: () => void;
}

const flowLevels = ['light', 'medium_light', 'medium', 'medium_heavy', 'heavy'];
const moodOptions = ['happy', 'neutral', 'sad', 'irritated', 'anxious', 'tired', 'energetic'];
const symptomOptions = [
  'cramps', 'headache', 'backache', 'nausea', 'fatigue', 'bloating', 
  'breast_tenderness', 'mood_swings', 'acne', 'spotting', 'cravings'
];

const CycleLogForm: React.FC<CycleLogFormProps> = ({ logId, selectedDate, onClose }) => {
  const { t } = useTranslation();
  const { logs, addLog, updateLog, loading } = useCycle();
  
  const [formData, setFormData] = useState({
    date: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
    period_start: false,
    flow_level: '',
    symptoms: [] as string[],
    mood: '',
    notes: '',
  });
  
  const [activeSection, setActiveSection] = useState(0);
  
  // Load data if editing
  useEffect(() => {
    if (logId) {
      const logToEdit = logs.find(log => log.id === logId);
      if (logToEdit) {
        setFormData({
          date: logToEdit.date,
          period_start: logToEdit.period_start,
          flow_level: logToEdit.flow_level || '',
          symptoms: logToEdit.symptoms || [],
          mood: logToEdit.mood || '',
          notes: logToEdit.notes || '',
        });
      }
    }
  }, [logId, logs]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    
    if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: (e.target as HTMLInputElement).checked,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };
  
  const handleSymptomChange = (symptom: string) => {
    setFormData(prev => {
      const symptoms = prev.symptoms.includes(symptom)
        ? prev.symptoms.filter(s => s !== symptom)
        : [...prev.symptoms, symptom];
      
      return { ...prev, symptoms };
    });
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const logData = {
        ...formData,
        flow_level: formData.flow_level || null,
        symptoms: formData.symptoms.length > 0 ? formData.symptoms : null,
        mood: formData.mood || null,
        notes: formData.notes || null,
      };

      if (logId) {
        await updateLog(logId, logData);
      } else {
        await addLog(logData);
      }
      onClose();
    } catch (err) {
      console.error('Error saving log', err);
    }
  };

  const sections = [
    { icon: Calendar, title: 'Basic Info' },
    { icon: Droplets, title: 'Flow & Period' },
    { icon: Heart, title: 'Symptoms & Mood' },
    { icon: FileText, title: 'Notes' }
  ];
  
  return (
    <motion.form 
      onSubmit={handleSubmit}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      {/* Progress Indicator - Mobile Optimized */}
      <div className="flex items-center justify-between mb-6 overflow-x-auto">
        {sections.map((section, index) => (
          <motion.button
            key={index}
            type="button"
            onClick={() => setActiveSection(index)}
            className={`flex flex-col sm:flex-row items-center space-y-1 sm:space-y-0 sm:space-x-2 px-2 sm:px-3 py-2 rounded-full transition-all duration-300 min-w-[60px] sm:min-w-auto ${
              activeSection === index
                ? 'bg-purple-100 text-purple-700'
                : 'text-gray-500 hover:text-purple-600'
            }`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <section.icon size={16} />
            <span className="text-xs sm:text-sm font-medium">{section.title}</span>
          </motion.button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {activeSection === 0 && (
          <motion.div
            key="basic"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-4"
          >
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-2">
                {t('cycle_log.date')}
              </label>
              <motion.input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                className="form-input-mobile"
                whileFocus={{ scale: 1.02 }}
                required
              />
            </div>
            
            <motion.div 
              className="flex items-center p-4 bg-pink-50 rounded-xl border border-pink-200"
              whileHover={{ scale: 1.01 }}
            >
              <input
                type="checkbox"
                id="period_start"
                name="period_start"
                checked={formData.period_start}
                onChange={handleChange}
                className="h-5 w-5 text-pink-600 border-pink-300 rounded focus:ring-pink-500"
              />
              <label htmlFor="period_start" className="ml-3 text-sm font-medium text-pink-800">
                {t('dashboard.period_start')}
              </label>
            </motion.div>
          </motion.div>
        )}

        {activeSection === 1 && (
          <motion.div
            key="flow"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-4"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                {t('cycle_log.flow_level')}
              </label>
              <div className="grid grid-cols-1 gap-2">
                {flowLevels.map(level => (
                  <motion.label
                    key={level}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer transition-all duration-300 min-h-[44px] ${
                      formData.flow_level === level
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-200 hover:border-purple-300 hover:bg-purple-25'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <input
                      type="radio"
                      name="flow_level"
                      value={level}
                      checked={formData.flow_level === level}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 rounded-full border-2 mr-3 ${
                      formData.flow_level === level
                        ? 'border-purple-500 bg-purple-500'
                        : 'border-gray-300'
                    }`}>
                      {formData.flow_level === level && (
                        <motion.div
                          className="w-2 h-2 bg-white rounded-full m-0.5"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                        />
                      )}
                    </div>
                    <span className="text-sm font-medium">
                      {t(`cycle_log.flow_${level}`)}
                    </span>
                  </motion.label>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {activeSection === 2 && (
          <motion.div
            key="symptoms"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                {t('dashboard.mood')}
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {moodOptions.map(mood => (
                  <motion.label
                    key={mood}
                    className={`flex items-center justify-center p-3 border rounded-xl cursor-pointer transition-all duration-300 min-h-[44px] ${
                      formData.mood === mood
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <input
                      type="radio"
                      name="mood"
                      value={mood}
                      checked={formData.mood === mood}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <span className="text-sm font-medium text-center">
                      {t(`moods.${mood}`)}
                    </span>
                  </motion.label>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                {t('cycle_log.symptoms')}
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {symptomOptions.map(symptom => (
                  <motion.label
                    key={symptom}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer transition-all duration-300 min-h-[44px] ${
                      formData.symptoms.includes(symptom)
                        ? 'border-pink-500 bg-pink-50 text-pink-700'
                        : 'border-gray-200 hover:border-pink-300'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <input
                      type="checkbox"
                      checked={formData.symptoms.includes(symptom)}
                      onChange={() => handleSymptomChange(symptom)}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 rounded border-2 mr-2 flex items-center justify-center ${
                      formData.symptoms.includes(symptom)
                        ? 'border-pink-500 bg-pink-500'
                        : 'border-gray-300'
                    }`}>
                      {formData.symptoms.includes(symptom) && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="w-2 h-2 bg-white rounded-sm"
                        />
                      )}
                    </div>
                    <span className="text-sm">
                      {t(`symptoms.${symptom}`)}
                    </span>
                  </motion.label>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {activeSection === 3 && (
          <motion.div
            key="notes"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                {t('cycle_log.notes')}
              </label>
              <motion.textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={4}
                className="form-input-mobile resize-none"
                placeholder={t('cycle_log.notes_placeholder')}
                whileFocus={{ scale: 1.01 }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <div className="flex flex-col sm:flex-row justify-between items-center pt-6 border-t border-gray-100 space-y-3 sm:space-y-0">
        <motion.button
          type="button"
          onClick={onClose}
          className="w-full sm:w-auto flex items-center justify-center space-x-2 btn-mobile text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-300"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <X size={16} />
          <span>{t('cycle_log.cancel')}</span>
        </motion.button>
        
        <motion.button
          type="submit"
          disabled={loading}
          className={`w-full sm:w-auto flex items-center justify-center space-x-2 btn-mobile bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-xl font-medium shadow-lg transition-all duration-300 ${
            loading ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-xl hover:scale-105'
          }`}
          whileHover={!loading ? { scale: 1.05 } : {}}
          whileTap={!loading ? { scale: 0.95 } : {}}
        >
          {loading ? (
            <motion.div
              className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
          ) : (
            <Save size={16} />
          )}
          <span>{loading ? t('cycle_log.saving') : t('cycle_log.save')}</span>
        </motion.button>
      </div>
    </motion.form>
  );
};

export default CycleLogForm;